import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {useKeyboard} from '../../../../hooks/keyboard';
import {useQuestion} from '../../../../hooks/question';
import {useMessage} from '../../../../hooks/message';
import {useRPC} from '../../../../hooks/rpc';
import {useSelector} from 'react-redux';
import {DoorBrandModel} from '../../../../types/door-brand';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';

const BACK_URL = '/developer/door-brand';

export const DoorBrandEditScreen = memo(function DoorBrandEditScreen() {
    const params = useParams<{ id: string }>();
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const question = useQuestion();
    const {success, failure} = useMessage();
    const [rpc] = useRPC();
    const history = useHistory();

    const doorList = useSelector(state => state.control.doorList);
    const doorBrandList = useSelector(state => state.control.doorBrandList);

    const [selected, setSelected] = useState<DoorBrandModel>(null);
    const [name, setName] = useState('');
    const [openCommandSuffix, setOpenCommandSuffix] = useState('');

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const isFieldsValid = useCallback((): boolean => {
        if (name == null || name.length === 0) {
            failure('Lütfen geçerli bir kamera adı giriniz.');

            return false;
        }

        return true;
    }, [name, failure]);

    const onSavePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        const doorBrandModel: DoorBrandModel = {
            id: selected?.id || null,
            name,
            door_open_command_suffix: openCommandSuffix,
        };

        return rpc.updateDoorBrand(doorBrandModel)
            .then(() => {
                success('Kapı markası başarılı bir şekilde ' + (doorBrandModel.id ? 'düzenlendi.' : 'oluşturuldu.'));
                onBackPressed();
            }).catch(failure);
    }, [
        isFieldsValid,
        selected?.id,
        name,
        openCommandSuffix,
        rpc,
        failure,
        success,
        onBackPressed,
    ]);

    const onDeletePressed = useCallback(() => {
        const filteredDoorList = doorList.filter(door => door.door_brand_id === selected.id);
        if (filteredDoorList.length !== 0) {
            if (filteredDoorList.length === 1) {
                failure(`${filteredDoorList[0].name} isimli kapı bu markayı kullanıyor. Lütfen kapıyı güncelleyip tekrar deneyiniz.`);
            } else {
                failure(`${filteredDoorList[0].name} isimli kapı ve diğer kapılar (${filteredDoorList.length - 1} adet) bu markayı kullanıyor. Lütfen kapıları güncelleyip tekrar deneyiniz.`);
            }

            return;
        }

        question('Silmek istediğinizden emin misiniz?', () => {
            return rpc.deleteDoorBrand(selected.id)
                .then(() => {
                    success('Kapı markası başarılı bir şekilde silindi.');
                    onBackPressed();
                })
                .catch(failure);
        });
    }, [doorList, failure, onBackPressed, question, rpc, selected?.id, success]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
    }, [setActiveInput]);

    const buttons = useMemo(() =>
        [
            {
                text: 'SİL',
                className: 'mulberry-wood',
                onClick: onDeletePressed,
                hidden: !(selected),
            },
            {
                text: 'KAYDET',
                className: 'green',
                onClick: onSavePressed,
            },
            {
                text: 'İPTAL',
                className: 'red',
                url: BACK_URL,
            },
        ], [selected, onSavePressed, onDeletePressed],
    );

    useEffect(() => {
        if (params.id == null) {
            return;
        }

        const doorBrand = doorBrandList.find(doorBrandItem => doorBrandItem.id === params['id']);
        if (doorBrand == null) {
            return;
        }

        setSelected(doorBrand);
        setName(doorBrand.name);
        setOpenCommandSuffix(doorBrand.door_open_command_suffix);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PasswordContainer requiredRole='developer' isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className='service-edit'
                backUrl={BACK_URL}
                currentInput={currentInput}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingInputComponent
                            placeholder='Kapı marka adı yazınız...'
                            label='Kapı Marka Adı'
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            maxLength={200}
                            placeholder='Kapı aç komutu yazınız...'
                            label='Kapı Aç Komutu'
                            value={openCommandSuffix}
                            setValue={setOpenCommandSuffix}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                    </div>
                    <div className='content-wrapper'>
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
        </PasswordContainer>
    );
});
